// A sale's invoice can be shown to the customer in another currency than the
// company's books (Settings' currency). The books stay in the Settings
// currency: every figure on the sale, Receivable, payments and reports is
// saved in it. The invoice only SHOWS the amounts converted at a rate fixed
// on that invoice: invoiceOptions.currency + invoiceOptions.rate
// ("1 invoice currency = rate × books currency"). Not set = the books
// currency, as before.

import { fmtMoney, findExchangeRate, type ExchangeRatePair } from './currency';

interface WithInvoiceCurrency {
  invoiceOptions?: { currency?: string; rate?: number } | null;
}

/** The invoice's currency (the books currency when none is set). */
export function invoiceCurrencyOf(inv: WithInvoiceCurrency | null | undefined, base: string): string {
  const c = inv?.invoiceOptions?.currency;
  return c && c !== base ? c : base;
}

/** Books-currency units per 1 invoice-currency unit; null when the invoice is in the books currency. */
export function invoiceRateOf(inv: WithInvoiceCurrency | null | undefined, base: string): number | null {
  const c = inv?.invoiceOptions?.currency;
  const r = inv?.invoiceOptions?.rate;
  return c && c !== base && r && r > 0 ? r : null;
}

/** A books-currency amount as it shows on the invoice ("€9,216.00"), or null when not foreign. */
export function fmtInInvoiceCurrency(amountBase: number, inv: WithInvoiceCurrency | null | undefined, base: string): string | null {
  const r = invoiceRateOf(inv, base);
  return r ? fmtMoney(amountBase / r, invoiceCurrencyOf(inv, base)) : null;
}

/** A starting rate for a new invoice currency: the Dashboard's exchange rate
 *  pairs, or the saved EUR rate for a € invoice of a $ company. */
export function suggestInvoiceRate(
  base: string, cur: string,
  pairs: ExchangeRatePair[] | undefined, eurUsdRate?: number,
): number | null {
  if (cur === base) return null;
  if (cur === 'EUR' && base === 'USD' && eurUsdRate && eurUsdRate > 0) return eurUsdRate;
  const r = findExchangeRate(pairs, cur, base);   // 1 cur = r base
  return r && r > 0 ? r : null;
}
